import { inject, injectable } from 'inversify';
import { TYPES } from '@/engine/ioc';
import type { ViewsService } from '@/engine/app/Views.service';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { IViewPortOrientation } from '@/engine/app/interfaces';
import { OrientationPresetKind, ViewPortKind } from '@/engine/app/interfaces';
import { v4 as uuid } from 'uuid';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity.ts';
import { getMaxIntFromTheEndOfString } from '@/engine/utils/stringUtils';
import {
  OrientationPresetsCommandType,
  type OrientationPresetsCommandsFactory
} from './commands/OrientationPresetCommands.factory';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { ICommand, ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import { TYPES as ImagesTYPES } from '@/engine/app/modules/images/iocTypes';
import { ImagesService } from '@/engine/app/modules/images/Images.service';
import { ImagesMode, SliceType } from '@/engine/app/modules/images/interfaces';
import { ViewController } from '@/engine/app/View.controller';
import { reaction } from 'mobx';
import { IDisposable } from '@/engine/Utilities/IDisposable';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer';

@injectable()
export class OrientationPresetsController implements IDisposable {
  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  private readonly _imagesService: ImagesService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _orientationPresetsCommandsFactory: OrientationPresetsCommandsFactory;

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _subscription: IDisposable;

  private _imagesReadySubscribtion?: IDisposable;

  public constructor(
    @inject(ImagesTYPES.ImagesService) imagesService: ImagesService,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(TYPES.OrientationPresetsCommandsFactory)
    orientationPresetsCommandsFactory: OrientationPresetsCommandsFactory,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor
  ) {
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._imagesService = imagesService;
    this._planningAlgorithm = planningAlgorithm;
    this._orientationPresetsCommandsFactory = orientationPresetsCommandsFactory;
    this._commandProcessor = commandProcessor;

    this._subscription = new AutoDisposer(
      reaction(
        () => this._viewsService.currentView,
        (currentView) => this._onCurrentViewChanged(currentView)
      )
    );
  }

  public dispose(): void {
    this._subscription.dispose();
    this._imagesReadySubscribtion?.dispose();
    this._imagesReadySubscribtion = undefined;
  }

  public async addPreset(label: string, orientation: IViewPortOrientation): Promise<void> {
    const currentView = this._viewsService.currentView;
    if (!currentView) return;

    const customDescriptor = 'CustomPresetOrientation' + uuid();
    const customLabel = `${label} ${this._getMaxCustomMeasurement(currentView) + 1}`;

    const command: ICommand = this._orientationPresetsCommandsFactory.createCommand(
      currentView,
      customLabel,
      customDescriptor,
      orientation,
      OrientationPresetsCommandType.AddPreset
    );
    await this._commandProcessor.execute(command);
  }

  public async deletePresetByDescriptor(descriptor: string): Promise<void> {
    const currentView = this._viewsService.currentViewOrThrow;

    const orientationPreset = currentView.getOrientationPresets().find((preset) => preset.descriptor === descriptor);
    if (!orientationPreset) return;

    const orientation = this._planningAlgorithm.getValueStrict(descriptor, DT.Orientation);

    const command: ICommand = this._orientationPresetsCommandsFactory.createCommand(
      currentView,
      orientationPreset.label,
      orientationPreset.descriptor,
      {
        topVector: orientation.value.top,
        directionVector: orientation.value.dir
      },
      OrientationPresetsCommandType.RemovePreset
    );
    await this._commandProcessor.execute(command);
  }

  public getOrientationByDescriptor(descriptor: string): DT.Orientation {
    return this._planningAlgorithm.getValueStrict(descriptor, DT.Orientation);
  }

  public set3DOrientationByDescriptor(descriptor: string): void {
    void this._applyOrientationByDescriptor(descriptor);
  }

  public applyOrientation(orientation: IViewPortOrientation, applyFor3d = true, applyForImages = true): void {
    const threeDViewPortController = this._viewController
      .getRegisteredViewPorts(ViewPortKind.ThreeD)
      .at(0)
      ?.getViewPortController();

    if (threeDViewPortController) {
      if (applyFor3d) {
        threeDViewPortController.setupCamera({
          topVector: orientation.topVector,
          directionVector: orientation.directionVector.clone().negate()
        });
        threeDViewPortController.fitCameraToObjects(this._viewsService.currentView?.getCameraFittingFactor());
      }

      if (applyForImages && this._imagesService.isReady()) {
        // TODO: MPR images mode should always deselct centerline!
        //   fix code duplications with:
        //     useImageModeHandlers.ts
        //     OrientationPresets.controller.ts
        //     cinelooControlController.ts
        //     viewModelSelectionReactionStrategiesProvider.ts
        this._viewController.setActiveCenterLineDescriptor(undefined);
        this._imagesService.setImagesMode(ImagesMode.MPR);

        this._imagesService.setSliceOrientation(SliceType.CORONAL, orientation.directionVector, orientation.topVector);

        const fitObjects = threeDViewPortController.getFitObjects();
        for (const vpm of this._viewController.getRegisteredViewPorts(ViewPortKind.ImageSlice)) {
          vpm.getViewPortController().getCameraController().fitCameraToObjects(fitObjects, { widthFittingFactor: 0.6 });
        }
      }
    }
  }

  private async _applyOrientationByDescriptor(
    descriptor: string,
    applyFor3d = true,
    applyForImages = true
  ): Promise<void> {
    const orientation = await this._planningAlgorithm.getValueAsync(descriptor, DT.Orientation);
    if (!orientation) {
      throw new Error('Orientation is not initialized');
    }
    this.applyOrientation(
      {
        topVector: orientation.value.top,
        directionVector: orientation.value.dir
      },
      applyFor3d,
      applyForImages
    );
  }

  private _getMaxCustomMeasurement(view: ViewEntity): number {
    const customMeasurements = view
      .getOrientationPresets()
      .filter((preset) => preset.kind === OrientationPresetKind.Custom)
      .map((m) => m.label);
    return getMaxIntFromTheEndOfString(customMeasurements);
  }

  private _onCurrentViewChanged(currentView: ViewEntity | null): void {
    this._imagesReadySubscribtion?.dispose();
    this._imagesReadySubscribtion = undefined;

    const orientationDescriptor = currentView?.getCameraOrientationDescriptor();

    if (!currentView || !orientationDescriptor) return;

    const orientationPresets = currentView.getOrientationPresets();
    if (orientationPresets.length == 0) return;

    if (this._imagesService.isReady()) {
      void this._applyOrientationByDescriptor(orientationDescriptor, false);
    } else {
      this._imagesReadySubscribtion = this._imagesService.subscribeToReadyStateChange((isReady) => {
        this._imagesReadySubscribtion?.dispose();
        this._imagesReadySubscribtion = undefined;
        if (isReady) {
          void this._applyOrientationByDescriptor(orientationDescriptor, false);
        }
      });
    }
  }
}
